import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import {
  HiArrowLeft,
  HiCheckCircle,
  HiClock,
  HiRefresh,
  HiShieldCheck,
} from "react-icons/hi";

import { VerifyOtpData } from "@/types/auth";

interface VerifyOtpFormProps {
  email: string;
  error?: string;
  isLoading?: boolean;
  isResending?: boolean;
  onBack: () => void;
  onResendOtp: () => void;
  onSubmit: (data: VerifyOtpData) => void;
}

export default function VerifyOtpForm({
  email,
  error,
  isLoading = false,
  isResending = false,
  onBack,
  onResendOtp,
  onSubmit,
}: VerifyOtpFormProps) {
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [timeLeft, setTimeLeft] = useState(300); // 5 minutes
  const [canResend, setCanResend] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const {
    formState: { errors },
    handleSubmit,
    setValue,
  } = useForm<VerifyOtpData>({
    defaultValues: { email },
  });

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          setCanResend(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const handleOtpChange = (index: number, value: string) => {
    if (value.length > 1) return; // Prevent multiple characters
    if (!/^\d*$/.test(value)) return; // Only allow digits

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Update form value
    const otpString = newOtp.join("");
    setValue("otp", otpString);
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const paste = e.clipboardData.getData("text");
    const pasteArray = paste.replace(/\D/g, "").split("").slice(0, 6);

    const newOtp = [...otp];
    pasteArray.forEach((digit, index) => {
      if (index < 6) {
        newOtp[index] = digit;
      }
    });
    setOtp(newOtp);
    setValue("otp", newOtp.join(""));

    // Focus the next empty input or the last one
    const nextIndex = Math.min(pasteArray.length, 5);
    inputRefs.current[nextIndex]?.focus();
  };

  const handleFormSubmit = () => {
    const otpString = otp.join("");
    if (otpString.length === 6) {
      onSubmit({ email, otp: otpString });
    }
  };

  const handleResend = () => {
    if (canResend && !isResending) {
      setTimeLeft(300);
      setCanResend(false);
      onResendOtp();
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const isOtpComplete =
    otp.every((digit) => digit !== "") && otp.join("").length === 6;

  return (
    <div className="card w-full max-w-md bg-base-100 shadow-xl">
      <div className="card-body">
        {/* Header */}
        <div className="mb-6 text-center">
          <div className="mb-4 flex justify-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-warning">
              <HiShieldCheck className="h-10 w-10 text-warning-content" />
            </div>
          </div>
          <h2 className="text-2xl font-bold text-base-content">Verify OTP</h2>
          <p className="mt-2 text-base-content/70">
            We&apos;ve sent a 6-digit code to
          </p>
          <p className="font-semibold text-primary">{email}</p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="alert alert-error mb-4">
            <span className="text-sm">{error}</span>
          </div>
        )}

        {/* OTP Input */}
        <form className="space-y-6" onSubmit={handleSubmit(handleFormSubmit)}>
          <div className="form-control">
            <label className="label">
              <span className="label-text">Enter 6-digit code</span>
            </label>
            <div className="mb-4 flex justify-center space-x-2">
              {otp.map((digit, index) => (
                <input
                  className={`input input-bordered h-12 w-12 text-center text-xl font-bold ${
                    digit ? "input-primary" : ""
                  } ${errors.otp ? "input-error" : ""}`}
                  disabled={isLoading}
                  inputMode="numeric"
                  key={index}
                  maxLength={1}
                  onChange={(e) => handleOtpChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onPaste={handlePaste}
                  ref={(el) => {
                    inputRefs.current[index] = el;
                  }}
                  type="text"
                  value={digit}
                />
              ))}
            </div>
            {errors.otp && (
              <label className="label">
                <span className="label-text-alt text-error">
                  Please enter a valid 6-digit code
                </span>
              </label>
            )}
          </div>

          {/* Timer and Resend */}
          <div className="space-y-3 text-center">
            {timeLeft > 0 ? (
              <div className="flex items-center justify-center text-base-content/70">
                <HiClock className="mr-2 h-4 w-4" />
                <span className="text-sm">
                  Code expires in {formatTime(timeLeft)}
                </span>
              </div>
            ) : (
              <div className="text-sm text-warning">
                Code has expired. Please request a new one.
              </div>
            )}

            <button
              className="btn btn-ghost btn-sm"
              disabled={!canResend || isResending}
              onClick={handleResend}
              type="button"
            >
              {isResending ? (
                <>
                  <span className="loading loading-spinner loading-sm mr-2"></span>
                  Sending...
                </>
              ) : (
                <>
                  <HiRefresh className="mr-2 h-4 w-4" />
                  Resend Code
                </>
              )}
            </button>
          </div>

          {/* Submit Button */}
          <button
            className="btn btn-primary w-full"
            disabled={!isOtpComplete || isLoading}
            type="submit"
          >
            {isLoading ? (
              <>
                <span className="loading loading-spinner loading-sm"></span>
                Verifying...
              </>
            ) : (
              <>
                <HiCheckCircle className="mr-2 h-4 w-4" />
                Verify Code
              </>
            )}
          </button>
        </form>

        {/* Back Button */}
        <div className="mt-6 text-center">
          <button className="btn btn-ghost btn-sm normal-case" onClick={onBack}>
            <HiArrowLeft className="mr-2 h-4 w-4" />
            Back to Email
          </button>
        </div>

        {/* Instructions */}
        <div className="mt-6 rounded-lg bg-base-200 p-4">
          <h4 className="mb-2 text-sm font-semibold">
            Didn&apos;t receive the code?
          </h4>
          <ul className="space-y-1 text-xs text-base-content/70">
            <li>• Check your spam/junk folder</li>
            <li>• Make sure the email address is correct</li>
            <li>• Wait a few minutes for delivery</li>
            <li>• Request a new code if expired</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
